import request from 'sb-request'
import actions from 'redux/actions'
import { getFeatureToggle } from 'helpers'
import { getState, reducers } from 'redux/core'
import { constants, getMainPageLink } from 'common/helpers'

import recommendationsQuery from './recommendationsQuery.graphql'
import reviewsQuery from './reviewsQuery.graphql'
import rateReviewMutation from './rateReviewMutation.graphql'
import rateReviewReplyMutation from './rateReviewReplyMutation.graphql'
import replyReviewMutation from './replyReviewMutation.graphql'


const reset = () => reducers.view.productPage.reset()

const redirectToMainPage = () => {
  const state = getState()
  const link = getMainPageLink({ state })

  actions.models.router.replace(link)
}

const getActiveVolumeIndex = (volumes) => {
  const {
    routing: {
      location: { hash },
    },
    view: {
      productPage: { type },
    },
  } = getState()

  const inStockVolumes = volumes.filter(({ inStock }) => inStock)

  if (hash === '#ecommerce' && inStockVolumes.length > 1) {
    return type === 'Perfume' ? 1 : inStockVolumes.length - 1
  }

  return 0
}

const fetch = (productSlug) => (
  actions.models.product.fetch(productSlug)
    .then(
      (result) => {
        const { volumes } = result

        const activeVolumeIndex = getActiveVolumeIndex(volumes)

        reducers.view.productPage.set({ ...result, activeVolumeIndex })

        return result
      },
      () => {
        // backend returns null and 404 if product has 'visibility' === 'INVISIBLE' or doesn't exists
        // backend may return it with errors so we need double check in modifyResult and reject handlers
        redirectToMainPage()

        return Promise.reject()
      }
    )
)

/**
 * @param {number} productId
 */
const fetchRecommendations = (productId) => {
  const modifyResult = (response = {}) => {
    response.categories = response.categories.map(({ name, recommenderName, ...rest }, index) => {
      let metadata
      let snowplowMetadata

      if (name === 'People who wear this also liked') {
        metadata = {
          placement: 'product page',
          tab: 'similar',
          recommenderName,
        }
        snowplowMetadata = {
          placement: 'recommendations',
          sectionTab: 'similar',
          recommenderName,
        }
      }
      else if (/^More (scents|products) from.*/.test(name)) {
        metadata = {
          placement: 'product page',
          tab: 'brand',
          recommenderName,
        }
        snowplowMetadata = {
          placement: 'recommendations',
          sectionTab: 'brand',
          recommenderName,
        }
      }
      else if (name === 'Extra cases') {
        metadata = {
          placement: 'product page',
          tab: 'cases',
        }
        snowplowMetadata = {
          placement: 'recommendations',
          sectionTab: 'cases',
        }
      }
      else if (name === 'Best if used with') {
        metadata = {
          placement: 'product page',
          tab: 'best_used',
          recommenderName,
        }
        snowplowMetadata = {
          placement: 'recommendations',
          sectionTab: 'best_used',
          recommenderName,
        }
      }
      else if (name === 'Products we love') {
        metadata = {
          placement: 'product page',
          tab: 'we love',
          recommenderName,
        }
        snowplowMetadata = {
          placement: 'recommendations',
          sectionTab: 'we love',
          recommenderName,
        }
      }
      else if (/^More.*scents$/.test(name)) {
        const isNote  = index === 2
        const isType  = index === 3
        const tab     = isNote ? 'scent note' : isType ? 'scent type' : ''

        metadata = {
          placement: 'product page',
          tab,
          recommenderName,
        }
        snowplowMetadata = {
          placement: 'recommendations',
          sectionTab: tab,
          recommenderName,
        }
      }

      return {
        title: name,
        metadata,
        snowplowMetadata,
        ...rest,
      }
    })

    return response
  }

  return request.post('graphql?name=recommendations', {
    body: {
      query: recommendationsQuery,
      variables: {
        productId,
      },
    },
    modifyResult,
  })
    .then(({ categories }) => {
      reducers.view.productPage.setRecommendations(categories)
      return categories
    })
}

const setActiveVolumeIndex = (index) => reducers.view.productPage.setActiveVolumeIndex(index)

const getActiveVolume = (state) => {
  const { view: { productPage: { volumes, activeVolumeIndex } } } = state

  return volumes[activeVolumeIndex]
}

/**
 *
 * @param {number} productId
 */
const fetchReviews = (productId) => {
  const state = getState()
  const { view: { productPage: { reviewsData: { paginationCursor, sortBy, selectedRating } } } } = state

  const globals = window.__globals__

  const isGoogleBot = globals.device.isBot && getFeatureToggle(constants.features.isGoogleBot)

  reducers.view.productPage.setReviewsFetching(true)

  return request.post('graphql?name=productReviews', {
    body: {
      query: reviewsQuery,
      variables: {
        slug: String(productId),
        offset: paginationCursor,
        limit: isGoogleBot ? -1 : 20,
        sort: sortBy,
        rating: selectedRating,
        excludeComplainedReviews: false,
        isBot: isGoogleBot,
      },
    },
    // TODO: rework Reviews component – replace likeCount, dislikeCount with helpful object
    // and after remove this modifyResult
    modifyResult: (result) => {
      const { product: { ratings, reviewsCount, userReview, reviews } } = result

      const getObjectWithInjectedHelpful = (obj) => {
        const { helpful: { positive: likeCount, negative: dislikeCount, isUseful } } = obj

        return {
          ...obj,
          likeCount,
          dislikeCount,
          isUseful,
        }
      }

      const getUserReviewWithInjectedHelpful = (userReview) => ({
        ...getObjectWithInjectedHelpful(userReview),
        replies: userReview.replies ? userReview.replies.map((reply) => getObjectWithInjectedHelpful(reply)) : [],
      })

      return {
        rating: ratings,
        reviewsCount,
        userReview: userReview ? getUserReviewWithInjectedHelpful(userReview) : userReview,
        reviews: reviews ? reviews.map((review) => getUserReviewWithInjectedHelpful(review)) : reviews,
      }
    },
  })
    .then((result) => {
      reducers.view.productPage.setReviews(result)

      return result
    })
}

const voteForReview = ({ productId, reviewId, replyId, isUseful }) => {
  reducers.view.productPage.setReviewVoteFor({ reviewId, replyId, isUseful })

  let name
  let query
  let input

  if (replyId) {
    name = 'rateReviewReply'
    query = rateReviewReplyMutation
    input = { isUseful, replyId }
  }
  else {
    name = 'rateReview'
    query = rateReviewMutation
    input = { isUseful, reviewId }
  }

  return request.post(`graphql?name=${name}`, {
    body: {
      query,
      variables: {
        input,
      },
    },
    withErrorModal: false,
  })
}

const setPaginationCursor = (paginationCursor) => {
  const { view: { productPage: { id } } } = getState()

  reducers.view.productPage.setPaginationCursor(paginationCursor)

  return fetchReviews(id)
}

const setSortBy = (sortBy) => {
  reducers.view.productPage.setSortBy(sortBy)
  setPaginationCursor(0)
}

const setSelectedRating = (selectedRating) => {
  reducers.view.productPage.setSelectedRating(selectedRating)
  setPaginationCursor(0)
}

const setReplyFormShown = (reviewId) => reducers.view.productPage.setReplyFormShown(reviewId)

const sendReply = (reviewId, message) => {
  const { view: { productPage: { id: productId } } } = getState()

  const input = {
    reviewId,
    message,
  }

  return request.post('graphql?name=replyReview', {
    body: {
      query: replyReviewMutation,
      variables: {
        input,
      },
    },
  })
    .then(() => fetchReviews(productId))
}

const handleStockNotificationSubscriptionSuccess = () =>
  reducers.view.productPage.setIsSubscribedToStockNotification(true)

const setIsLandingVersionEnabled = (value) => reducers.view.productPage.setIsLandingVersionEnabled(value)

const setSource = (value) => reducers.view.productPage.setSource(value)

const setOldPathname = (value) => reducers.view.productPage.setOldPathname(value)

const setRecommenderName = (value) => reducers.view.productPage.setRecommenderName(value)


export default {
  reset,
  fetch,
  fetchRecommendations,
  setActiveVolumeIndex,
  getActiveVolume,
  fetchReviews,
  voteForReview,
  setPaginationCursor,
  setSortBy,
  setSelectedRating,
  setReplyFormShown,
  sendReply,
  handleStockNotificationSubscriptionSuccess,
  setIsLandingVersionEnabled,
  setSource,
  setOldPathname,
  setRecommenderName,
}
